import React, { useState, useEffect } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Row, Modal, Button, Form } from "react-bootstrap";
import { useQuery } from "react-query";
import Spinner from "../../../../../../Partials/Spinner";
import { toast } from "react-toastify";
import { getMenuForAdmin } from '../../../../../../hooks/menu/menu';
import {
  getMenuCycles,
  getWeeklyMenu,
  getMenuTemplates,
  useSaveMenuCycle,
  UpdateMenuCycle,
  UpdateWeeklyMenu,
  DeleteMenuCycleById
} from "../../../../../../hooks/admin/superiorAdmin";
import CycleSelector from './CycleSelector';
import CycleForm from './CycleForm';
import MealOptionsControls from './MealOptionsControls';
import WeeklyPlanner from './WeeklyPlanner';
import DishesMenu from './DishesMenu';
import ActivationModal from './ActivationModal';

function DnD() {
  const [cycleName, setCycleName] = useState('');
  const [loopInterval, setLoopInterval] = useState(6);
  const [isLooping, setIsLooping] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [weeklyMenuId, setWeeklyMenuId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [lunchCount, setLunchCount] = useState(1);
  const [supperCount, setSupperCount] = useState(1);
  const [weeks, setWeeks] = useState([]);
  const [newCycle, setNewCycle] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  const { data: menu } = useQuery(['getMenu', templateName], () => getMenuForAdmin(templateName), { enabled: !!templateName });
  const { data: menuCycles, refetch: refetchCycles } = useQuery('menuCycles', getMenuCycles);
  const { data: weeklyMenu } = useQuery(['weeklyMenu', weeklyMenuId], () => getWeeklyMenu(weeklyMenuId), { enabled: !!weeklyMenuId });
  const { data: menuTemplates, error, isLoading } = useQuery("getMenuTemplates", getMenuTemplates);

  const { saveBothMenus, weeklyMenuMutation, menuCycleMutation } = useSaveMenuCycle();
  const { mutate: updateCycleMutation } = UpdateMenuCycle();
  const { mutate: updateWeeklyMenuMutation } = UpdateWeeklyMenu();
  const { mutate: deleteMenuCycleById } = DeleteMenuCycleById();

  useEffect(() => {
    if (weeklyMenu?.data?.menu?.weeks) {
      const initializedWeeks = weeklyMenu.data.menu.weeks.map(week => ({
        ...week,
        weekName: cycleName || week.weekName || `Vecka ${week.weekNumber}`,
        weekColor: week.weekColor || '#000000',
        days: week.days.map(day => ({
          ...day,
          supper: selectedGroup === "senior" ? (day.supper && day.supper.length > 0 ? day.supper : [{ alt: 0, dish: null, side: [] }]) : [],
          dessert: selectedGroup === "senior" ? (day.dessert && day.dessert.length > 0 ? day.dessert : [{ alt: 0, dish: null, side: [] }]) : []
        }))
      }));
      setWeeks(initializedWeeks);
      setLoopInterval(weeklyMenu.data.menu.weeks.length);
      if (!newCycle) {
        const firstWeek = weeklyMenu.data.menu.weeks[0];
        if (firstWeek) {
          setLunchCount(firstWeek.days[0]?.lunch?.length || 1);
          setSupperCount(firstWeek.days[0]?.supper?.length || 1);
        }
      }
      if (weeklyMenu.data.startDate) {
        setStartDate(weeklyMenu.data.startDate);
      }
    }
  }, [weeklyMenu, selectedGroup, cycleName, newCycle]);

  const duplicateWeeklyMenu = () => {
    if (!cycleId || !weeklyMenuId) {
      toast.error('Välj en cykel att duplicera');
      return;
    }

    const selectedCycle = menuCycles?.data?.MenuCycle.find(c => c._id === cycleId);
    if (!selectedCycle) {
      toast.error('Cykeln hittades inte');
      return;
    }

    const duplicatedWeeks = weeks.map(week => ({
      weekNumber: week.weekNumber,
      weekName: week.weekName,
      weekColor: week.weekColor,
      days: week.days.map(day => ({
        day: day.day,
        lunch: day.lunch.map(meal => ({
          alt: meal.alt ?? 0,
          dish: meal.dish,
          side: meal.side
        })),
        ...(selectedGroup === "senior" ? {
          supper: day.supper.map(meal => ({
            alt: meal.alt ?? 0,
            dish: meal.dish,
            side: meal.side
          })),
          dessert: day.dessert.map(meal => ({
            alt: meal.alt ?? 0,
            dish: meal.dish,
            side: meal.side
          }))
        } : {})
      }))
    }));

    setNewCycle(true);
    setCycleName(`${selectedCycle.cycleName} (Kopia)`);
    setLoopInterval(selectedCycle.loopInterval);
    setIsLooping(false);
    setTemplateId(selectedCycle.templateId);
    setSelectedGroup(selectedCycle.group);
    setWeeks(duplicatedWeeks);
    setCycleId('');
    setWeeklyMenuId('');
    setTemplateName(menuTemplates?.data?.templates.find(t => t._id === selectedCycle.templateId)?.templateName || '');
    setShowModal(false);
    toast.info('Cykel duplicerad, redigera och spara den nya cykeln.');
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      toast.warn('Släpp maträtten i en giltig zon');
      return;
    }

    if (source.droppableId === 'dishesMenu' || source.droppableId === 'dessertsMenu') {
      const parts = destination.droppableId.split('-');

      if (parts.length !== 8) {
        toast.error('Ogiltigt droppableId-format');
        return;
      }

      const weekIndex = parseInt(parts[1], 10);
      const dayIndex = parseInt(parts[3], 10);
      const mealType = parts[5];
      const altIndex = parseInt(parts[7], 10);

      if (isNaN(weekIndex) || isNaN(dayIndex) || isNaN(altIndex)) {
        toast.error('Ogiltiga index i droppableId');
        return;
      }

      const droppedDish = menu?.data[0]?.menu?.find(d => d.dishName === draggableId);
      if (!droppedDish) {
        toast.error('Maträtten hittades inte');
        return;
      }

      if (mealType === 'dessert' && !droppedDish?.isDessert) {
        toast.error('Endast efterrätter kan släppas i efterrättsområdet');
        return;
      }

      if ((mealType === 'lunch' || mealType === 'supper') && droppedDish?.isDessert) {
        toast.error('Efterrätter kan inte släppas i lunch- eller middagsområden');
        return;
      }

      if ((mealType === 'supper' || mealType === 'dessert') && selectedGroup !== "senior") {
        toast.error(`${mealType === 'supper' ? 'Middag' : 'Efterrätt'} är endast tillåten för seniorgruppen`);
        return;
      }

      const defaultSide = droppedDish?.sideIngredient || "";
      const extraSides = droppedDish?.extraSideIngredient?.map(extra => extra.extraSideIngredient) || [];
      const sideDish = [defaultSide, ...extraSides].filter(Boolean);

      setWeeks(prevWeeks => {
        const newWeeks = JSON.parse(JSON.stringify(prevWeeks));
        if (!newWeeks[weekIndex]?.days[dayIndex]?.[mealType]) {
          toast.error('Ogiltig state-struktur');
          return prevWeeks;
        }
        newWeeks[weekIndex].days[dayIndex][mealType][altIndex] = {
          alt: altIndex,
          dish: droppedDish._id,
          side: sideDish,
        };
        return newWeeks;
      });
    } else {
      console.log('Invalid source droppableId:', source.droppableId);
    }
  };

  const handleDelete = ({ cycleId, deleteMenuCycleById, refetchCycles, setCycleId, setWeeks, setShowModal }) => {
    const selectedCycle = menuCycles?.data?.MenuCycle.find(c => c._id === cycleId);
    if (selectedCycle?.isLooping) {
      toast.warn('Cykeln är aktiv. Vänligen inaktivera den först i "Active & Datum".');
      setShowModal(true);
      return;
    }

    if (deleteConfirmInput !== selectedCycle?.cycleName) {
      toast.error('Felaktigt cykelnamn. Försök igen.');
      return;
    }

    deleteMenuCycleById(cycleId, {
      onSuccess: () => {
        toast('Cycle deleted successfully!');
        refetchCycles();
        setCycleId('');
        setWeeks([]);
        setShowDeleteConfirm(false);
        setDeleteConfirmInput('');
      },
      onError: (error) => {
        console.error('deleteMenuCycleById error:', error);
        toast.error(`Fel vid radering av cykel: ${error.response?.data?.error || error.message || 'Okänt fel'}`);
      },
    });
  };

  if (error) return <div>Nånting gick fel</div>;
  if (isLoading) return <div style={{ height: '100vh' }}><Spinner /></div>;

  return (
    <div className="App">
      <h4 className="text-light mb-4">Menycykelplanerare</h4>
      <CycleSelector
        menuCycles={menuCycles}
        setCycleId={setCycleId}
        setWeeklyMenuId={setWeeklyMenuId}
        setCycleName={setCycleName}
        setLoopInterval={setLoopInterval}
        setIsLooping={setIsLooping}
        setTemplateId={setTemplateId}
        setSelectedGroup={setSelectedGroup}
        setNewCycle={setNewCycle}
        newCycle={newCycle}
        setWeeks={setWeeks}
        setTemplateName={setTemplateName}
        setShowModal={setShowModal}
        lunchCount={lunchCount}
        supperCount={supperCount}
        setLunchCount={setLunchCount}
        setSupperCount={setSupperCount}
      />
      <DragDropContext onDragEnd={onDragEnd}>
        {(newCycle || cycleId) && (
          <>
            <CycleForm
              newCycle={newCycle}
              cycleName={cycleName}
              setCycleName={setCycleName}
              loopInterval={loopInterval}
              setLoopInterval={setLoopInterval}
              templateName={templateName}
              setTemplateName={setTemplateName}
              setTemplateId={setTemplateId}
              setSelectedGroup={setSelectedGroup}
              menuTemplates={menuTemplates}
              saveBothMenus={saveBothMenus}
              weeklyMenuMutation={weeklyMenuMutation}
              menuCycleMutation={menuCycleMutation}
              refetchCycles={refetchCycles}
              templateId={templateId}
              selectedGroup={selectedGroup}
              weeks={weeks}
            />
            <MealOptionsControls
              lunchCount={lunchCount}
              setLunchCount={setLunchCount}
              supperCount={supperCount}
              setSupperCount={setSupperCount}
              setWeeks={setWeeks}
            />
            <div className="planner-container">
              <Row>
                <WeeklyPlanner
                  weeks={weeks}
                  setWeeks={setWeeks}
                  selectedGroup={selectedGroup}
                  loopInterval={loopInterval}
                  newCycle={newCycle}
                  cycleId={cycleId}
                  lunchCount={lunchCount}
                  supperCount={supperCount}
                  dishes={menu?.data[0]?.menu}
                  cycleName={cycleName}
                />
                <DishesMenu dishes={menu?.data[0]?.menu} weeks={weeks} />
              </Row>
            </div>
            {!newCycle && cycleId && (
              <div className="d-flex gap-3">
                <button className="mainButtonLite text-light" onClick={() => handleUpdate({
                  weeklyMenuId, weeks, updateWeeklyMenuMutation, refetchCycles, selectedGroup, templateId
                })}>
                  Uppdatera
                </button>
                <button className="mainButtonLite text-danger" onClick={() => setShowDeleteConfirm(true)}>
                  Radera
                </button>
              </div>
            )}
          </>
        )}
      </DragDropContext>
      <ActivationModal
        show={showModal}
        setShow={setShowModal}
        cycleId={cycleId}
        setCycleId={setCycleId}
        startDate={startDate}
        setStartDate={setStartDate}
        isLooping={isLooping}
        setIsLooping={setIsLooping}
        menuCycles={menuCycles}
        selectedGroup={selectedGroup}
        updateCycleMutation={updateCycleMutation}
        duplicateWeeklyMenu={duplicateWeeklyMenu}
        cycleName={cycleName}
        loopInterval={loopInterval}
        templateId={templateId}
      />
      <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setDeleteConfirmInput(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bekräfta Radering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Ange cykelnamnet för att bekräfta radering av &quot;{menuCycles?.data?.MenuCycle.find(c => c._id === cycleId)?.cycleName}&quot;:</Form.Label>
            <Form.Control
              type="text"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              placeholder="Cykelnamn"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmInput(''); }}>
            Avbryt
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete({ cycleId, deleteMenuCycleById, refetchCycles, setCycleId, setWeeks, setShowModal })}
          >
            Radera
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const handleUpdate = async ({ weeklyMenuId, weeks, updateWeeklyMenuMutation, refetchCycles, selectedGroup, templateId }) => {
  const sanitizedWeeks = weeks.map(week => ({
    weekNumber: week.weekNumber,
    weekName: week.weekName,
    weekColor: week.weekColor,
    days: week.days.map(day => ({
      day: day.day,
      lunch: day.lunch.map(meal => ({
        alt: meal.alt ?? 0,
        dish: meal.dish,
        side: meal.side
      })),
      ...(selectedGroup === "senior" ? {
        supper: day.supper.map(meal => ({
          alt: meal.alt ?? 0,
          dish: meal.dish,
          side: meal.side
        })),
        dessert: day.dessert.map(meal => ({
          alt: meal.alt ?? 0,
          dish: meal.dish,
          side: meal.side
        }))
      } : {})
    }))
  }));

  const weeklyMenuData = {
    weeks: sanitizedWeeks,
    group: selectedGroup,
    templateId: templateId
  };

  try {
    await updateWeeklyMenuMutation({ weeklyMenuId, weeklyMenuData }, {
      onSuccess: () => {
        toast.success('Vecko meny uppdaterad!');
        refetchCycles();
      },
      onError: (error) => {
        console.error('updateWeeklyMenuMutation error:', error);
        console.error('Error details:', error.response?.data || error.message);
        toast.error(`Fel vid uppdatering av veckomeny: ${error.response?.data?.error || error.message || 'Okänt fel'}`);
      },
    });
  } catch (error) {
    console.error('handleUpdate error:', error);
    toast.error(`Fel vid sparande av data: ${error.message || 'Okänt fel'}`);
  }
};

export default DnD;